import { Component, input, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import type { FreezeCycleAdjustmentResponse } from '../../models/freeze-reason.model';

const VISIBLE_ITEMS_COUNT = 3;

@Component({
  selector: 'freeze-cycle-adjustment-details',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SkeletonModule,
    TagModule,
    CurrencyformatPipe,
    DateTimeFormatPipe
  ],
  templateUrl: './freeze-cycle-adjustment-details.component.html',
  styleUrls: ['./freeze-cycle-adjustment-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FreezeCycleAdjustmentDetailsComponent {
  readonly cycleInfo = input<FreezeCycleAdjustmentResponse | null>(null);
  readonly isLoading = input<boolean>(false);
  readonly showTitle = input<boolean>(true);

  readonly billingCyclesExpanded = signal(false);
  readonly adjustedCyclesExpanded = signal(false);

  readonly visibleItemsCount = VISIBLE_ITEMS_COUNT;

  readonly billingCyclesVisible = computed(() => {
    const cycles = this.cycleInfo()?.freeze_billing_cycle ?? [];
    return this.billingCyclesExpanded() ? cycles : cycles.slice(0, VISIBLE_ITEMS_COUNT);
  });

  readonly adjustedCyclesVisible = computed(() => {
    const cycles = this.cycleInfo()?.freeze_adjusted_cycle ?? [];
    return this.adjustedCyclesExpanded() ? cycles : cycles.slice(0, VISIBLE_ITEMS_COUNT);
  });

  readonly billingCyclesHasMore = computed(() =>
    (this.cycleInfo()?.freeze_billing_cycle?.length ?? 0) > VISIBLE_ITEMS_COUNT
  );

  readonly adjustedCyclesHasMore = computed(() =>
    (this.cycleInfo()?.freeze_adjusted_cycle?.length ?? 0) > VISIBLE_ITEMS_COUNT
  );

  readonly billingCyclesHiddenCount = computed(() =>
    Math.max(0, (this.cycleInfo()?.freeze_billing_cycle?.length ?? 0) - VISIBLE_ITEMS_COUNT)
  );

  readonly adjustedCyclesHiddenCount = computed(() =>
    Math.max(0, (this.cycleInfo()?.freeze_adjusted_cycle?.length ?? 0) - VISIBLE_ITEMS_COUNT)
  );

  toggleBillingCycles(): void {
    this.billingCyclesExpanded.update(v => !v);
  }

  toggleAdjustedCycles(): void {
    this.adjustedCyclesExpanded.update(v => !v);
  }

  hasCycleData(): boolean {
    const info = this.cycleInfo();
    return !!(info && (
      (info.freeze_billing_cycle && info.freeze_billing_cycle.length > 0) ||
      (info.freeze_adjusted_cycle && info.freeze_adjusted_cycle.length > 0)
    ));
  }

  getTotalFreezeDays(): number {
    const info = this.cycleInfo();
    if (!info || !info.freeze_billing_cycle) return 0;
    return info.freeze_billing_cycle.reduce((total, cycle) => total + (cycle.freeze_days || 0), 0);
  }

  getTotalAdjustmentAmount(): number {
    const info = this.cycleInfo();
    if (!info || !info.freeze_billing_cycle) return 0;
    return info.freeze_billing_cycle.reduce((total, cycle) => total + (cycle.adjustment_amount_in_minor_currency || 0), 0);
  }
}
